import React, {
  useState,
  useMemo,
  useCallback,
  createContext,
  useContext,
} from 'react';
import once from 'lodash.once';
import { useNotificationCenter } from '../toast';

interface IFormContext<T extends Record<string, any>> {
  formData: T;
  setFormData: (formData: T) => void;
  // errors?: string[];
  onChangeHandler: <R>(name: string, value: R) => void;
  onErrorHandler: (
    err: string | null,
    success: boolean,
    cb: () => void,
  ) => void;
  // onSubmit?: () => void;
  onReset: () => void;
}

interface IFormProps<T> {
  name: string;
  initialValues: T;
  children: React.ReactNode;
}

const createFormContext = once(<T extends Record<string, any>>() =>
  createContext<IFormContext<T> | undefined>(undefined),
);

function FormsProvider<T extends Record<string, any>>({
  initialValues,
  name,
  children,
}: IFormProps<T>) {
  const FormContext = createFormContext<T>();
  FormContext.displayName = name;

  const [formState, setFormState] = useState<T>(initialValues);
  const { addNotification } = useNotificationCenter();

  const handleOnChange = useCallback(
    <R,>(name: string, value: R) => {
      setFormState({ ...formState, [name]: value });
    },
    [formState],
  );

  const handelSetFormState = useCallback((formData: T) => {
    setFormState(formData);
  }, []);

  const handleOnReset = useCallback(() => {
    setFormState(initialValues);
  }, [initialValues]);

  const handleOnError = useCallback(
    (err: string | null, success: boolean, cb: () => void) => {
      if (!success) {
        addNotification(
          {
            title: 'ERROR',
            description: err || 'There was an error',
          },
          {
            variant: 'ERROR',
          },
        );

        return;
      }

      cb();
    },
    [addNotification],
  );

  const memoContextValue = useMemo(
    (): IFormContext<T> => ({
      formData: formState,
      setFormData: handelSetFormState,
      onChangeHandler: handleOnChange,
      onReset: handleOnReset,
      onErrorHandler: handleOnError,
    }),
    [
      formState,
      handelSetFormState,
      handleOnChange,
      handleOnReset,
      handleOnError,
    ],
  );

  return (
    <FormContext.Provider value={memoContextValue}>
      {children}
    </FormContext.Provider>
  );
}

export const useFormContext = <
  T extends Record<string, any>
>(): IFormContext<T> => {
  const context = useContext(createFormContext<T>());
  if (!context) {
    throw new Error(
      'This component must be used within a <FormsProvider> component',
    );
  }

  return context;
};

export { FormsProvider };
